import { useEffect } from "react";
import {
  Button,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { EventDetailAccessCodesSection } from "@common/interfaces/KYDEvent";
import { FaCheckCircle } from "react-icons/fa";
import { KYDTicketTypeAccessCode } from "@common/interfaces/KYDTicket";
import { getRandomString } from "../../../utils";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DatepickerFormInput from "../TicketTypes/DatepickerFormInput";

type AccessCodeValues = {
  id: string;
  code: string;
  valid_from?: string;
  valid_to?: string;
  ticket_type_ids: string[];
  status: string;
};

const EditAccessCodeModal = ({
  data,
  link,
  accessCode,
  timezone,
  onClose,
  loading,
  onSubmit,
  isOpen = false,
}: {
  link?: string;
  timezone?: string;
  isOpen?: boolean;
  loading: Record<string, boolean>;
  onClose: () => void;
  data: EventDetailAccessCodesSection["data"];
  accessCode: KYDTicketTypeAccessCode | null;
  onSubmit: (data: AccessCodeValues) => Promise<void>;
}) => {
  const methods = useForm<AccessCodeValues>({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    values: {
      id: accessCode ? accessCode.id : undefined,
      code: accessCode ? accessCode.code : "",
      status: accessCode ? accessCode.status : "DISABLED",
      ticket_type_ids: accessCode ? accessCode.ticket_type_ids : [],
      valid_from: accessCode ? accessCode.valid_from || null : null,
      valid_to: accessCode ? accessCode.valid_to || null : null,
    },
  });
  const { control, watch, reset, setValue, register, handleSubmit } = methods;
  const code = watch("code");
  const status = watch("status", "DISABLED");

  const _onClose = () => {
    reset();
    onClose();
  };

  const _onSubmit = (data: AccessCodeValues) => {
    onSubmit(data);
  };

  useEffect(() => {
    if (accessCode) {
      reset();
    }
  }, [accessCode]);

  return (
    <Modal
      size={"xl"}
      isOpen={isOpen}
      onClose={_onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {accessCode ? "Edit Access Code" : "Create Access Code"}
        </ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <Stack spacing={4}>
              <HStack alignItems={"center"} justifyContent={"space-between"}>
                <Stack spacing={0}>
                  <Text fontWeight={"medium"}>Code</Text>
                  <HStack>
                    <InputGroup size={"md"}>
                      <Input
                        pr="6rem"
                        {...register("code")}
                        isDisabled={loading["accesscode"]}
                      />
                      {!code ? (
                        <InputRightElement width="6rem">
                          <Button
                            onClick={() => {
                              const s = getRandomString(10).toUpperCase();
                              setValue("code", s, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }}
                            h="1.75rem"
                            size="sm"
                          >
                            Generate
                          </Button>
                        </InputRightElement>
                      ) : null}
                    </InputGroup>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(`${link}?code=${code}`);
                      }}
                      h="1.75rem"
                      size="sm"
                    >
                      Copy link
                    </Button>
                  </HStack>
                </Stack>
                <Stack spacing={0} flexGrow={1} marginLeft={2}>
                  <Text fontWeight={"medium"}>Enabled</Text>
                  <HStack>
                    <Text>{status === "OPEN" ? "Yes" : "No"}</Text>
                    <Switch
                      isChecked={status === "OPEN"}
                      checked={status === "OPEN"}
                      defaultChecked={status === "OPEN"}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setValue("status", "OPEN", {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          return;
                        }
                        setValue("status", "DISABLED", {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      size={"lg"}
                      colorScheme={"green"}
                    />
                  </HStack>
                </Stack>
              </HStack>

              <HStack width={"full"}>
                <Stack flex={1}>
                  <Text fontWeight={"medium"}>Valid from:</Text>
                  <DatepickerFormInput
                    skipValidate
                    name="valid_from"
                    timezone={timezone}
                    dateTimeProps={{
                      slotProps: {
                        actionBar: {
                          actions: ["clear", "accept"],
                        },
                      },
                      closeOnSelect: true,
                    }}
                  />
                </Stack>
                <Stack flex={1}>
                  <Text fontWeight={"medium"}>Valid to:</Text>
                  <DatepickerFormInput
                    skipValidate
                    name="valid_to"
                    timezone={timezone}
                    dateTimeProps={{
                      slotProps: {
                        actionBar: {
                          actions: ["clear", "accept"],
                        },
                      },
                      closeOnSelect: true,
                    }}
                  />
                </Stack>
              </HStack>

              <Stack spacing={0}>
                <Text fontWeight={"medium"}>Ticket Types</Text>
                <Controller
                  control={control}
                  defaultValue={[]}
                  name="ticket_type_ids"
                  render={({ field }) => (
                    <Stack
                      rounded={"lg"}
                      borderWidth={"1px"}
                      borderColor={"gray.300"}
                      p={2}
                      maxH={"300px"}
                      overflowY={"scroll"}
                    >
                      {data.ticket_types
                        .concat(data.tiered_ticket_types)
                        .map((tt) => {
                          const prevValue = field.value?.find(
                            (selected) => selected === tt.id
                          );

                          return (
                            <HStack
                              justify={"space-between"}
                              h="50px"
                              p={2}
                              rounded={"lg"}
                              borderWidth={"1px"}
                              key={`${tt.id}-${field.value?.length}`}
                              cursor={"pointer"}
                              onClick={() => {
                                const otherValues =
                                  field.value?.filter(
                                    (selectedTt) => selectedTt !== tt.id
                                  ) || [];

                                setValue(
                                  "ticket_type_ids",
                                  otherValues.concat(prevValue ? [] : tt.id)
                                );
                              }}
                            >
                              <Text fontWeight={prevValue ? "bold" : "normal"}>
                                {tt.name}
                              </Text>
                              <Icon
                                display={prevValue ? "inherit" : "none"}
                                boxSize={8}
                                color={"green.500"}
                                as={FaCheckCircle}
                              />
                            </HStack>
                          );
                        })}
                    </Stack>
                  )}
                />
              </Stack>

              <Stack spacing={0}>
                <Text fontWeight={"medium"}>Ticket Holds</Text>
                <Controller
                  control={control}
                  defaultValue={[]}
                  name="ticket_type_ids"
                  render={({ field }) => (
                    <Stack
                      rounded={"lg"}
                      borderWidth={"1px"}
                      borderColor={"gray.300"}
                      p={2}
                      maxH={"300px"}
                      overflowY={"scroll"}
                    >
                      {data.ticket_holds
                        .filter((hold) => hold.status === "OPEN")
                        .map((hold) => {
                          const prevValue = field.value?.find(
                            (selected) => selected === hold.id
                          );

                          return (
                            <HStack
                              justify={"space-between"}
                              h="50px"
                              p={2}
                              rounded={"lg"}
                              borderWidth={"1px"}
                              key={`${hold.id}-${field.value?.length}`}
                              cursor={"pointer"}
                              onClick={() => {
                                const otherValues =
                                  field.value?.filter(
                                    (selectedHold) => selectedHold !== hold.id
                                  ) || [];

                                setValue(
                                  "ticket_type_ids",
                                  otherValues.concat(prevValue ? [] : hold.id)
                                );
                              }}
                            >
                              <Text fontWeight={prevValue ? "bold" : "normal"}>
                                {hold.name}
                              </Text>
                              <Icon
                                display={prevValue ? "inherit" : "none"}
                                boxSize={8}
                                color={"green.500"}
                                as={FaCheckCircle}
                              />
                            </HStack>
                          );
                        })}
                    </Stack>
                  )}
                />
              </Stack>
            </Stack>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <Button isDisabled={loading["accesscode"]} onClick={_onClose} mr="4">
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            _hover={{
              backgroundColor: "green.600",
            }}
            isLoading={loading["accesscode"]}
            onClick={handleSubmit(_onSubmit)}
          >
            {accessCode ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditAccessCodeModal;
